import { useEffect } from 'react';
import { Box } from './UI/Box';
import { Flex } from './UI/Flex';
import Typography from './Typography';
import { Button } from './UI/Button';
import { PRESCRIPTION_ITEM_ICON, PrescriptionPreviewItem } from 'src/services/prescriber';
import { useIsMobile } from 'src/helpers/useIsMobile';
import { IconButton } from './UI/IconButton';

import MedicineRedStripIcon from './Icons/MedicineRedStripIcon';
import MedicineRedStripGenericIcon from './Icons/MedicineRedStripGenericIcon';
import MedicineBlackStripIcon from './Icons/MedicineBlackStripIcon';
import MedicineBlackStripGenericIcon from './Icons/MedicineBlackStripGenericIcon';
import MedicineBlueStripIcon from './Icons/MedicineBlueStripIcon';
import MedicineBlueStripGenericIcon from './Icons/MedicineBlueStripGenericIcon';
import ProductIcon from './Icons/ProductIcon';
import BakerIcon from './Icons/BakerIcon';
import CertificateIcon from './Icons/CertificateIcon';
import ForwardingIcon from './Icons/ForwardingIcon';
import DocBoxIcon from './Icons/DocBoxIcon';
import { ReactComponent as CloseSVG } from '../assets/icons/close.svg';
import { type IconProps } from './Icons/_IconPattern';

interface PrescriptionPreviewModalProps {
  medicines: PrescriptionPreviewItem[];
  exams: PrescriptionPreviewItem[];
  referrals: PrescriptionPreviewItem[];
  medicalCertificates: PrescriptionPreviewItem[];
  reports: PrescriptionPreviewItem[];
  onNewPrescription: () => void;
  onResumePrescription: () => void;
  onClose?: () => void;
  isLoading?: boolean;
}

const medicineIconMap: Record<PRESCRIPTION_ITEM_ICON, React.FunctionComponent<IconProps>> = {
  [PRESCRIPTION_ITEM_ICON.RED_STRIP]: MedicineRedStripIcon,
  [PRESCRIPTION_ITEM_ICON.RED_STRIP_GENERIC]: MedicineRedStripGenericIcon,
  [PRESCRIPTION_ITEM_ICON.BLACK_STRIP]: MedicineBlackStripIcon,
  [PRESCRIPTION_ITEM_ICON.BLACK_STRIP_GENERIC]: MedicineBlackStripGenericIcon,
  [PRESCRIPTION_ITEM_ICON.BLUE_STRIP]: MedicineBlueStripIcon,
  [PRESCRIPTION_ITEM_ICON.BLUE_STRIP_GENERIC]: MedicineBlueStripGenericIcon,
  [PRESCRIPTION_ITEM_ICON.PRODUCT_CORRELATED]: ProductIcon,
  [PRESCRIPTION_ITEM_ICON.BEAKER]: BakerIcon,
  [PRESCRIPTION_ITEM_ICON.CERTIFICATE]: CertificateIcon,
  [PRESCRIPTION_ITEM_ICON.FORWARDING]: ForwardingIcon,
  [PRESCRIPTION_ITEM_ICON.DOC]: DocBoxIcon,
};

const getIconForItem = (icon: PRESCRIPTION_ITEM_ICON, width: number = 24) => {
  const IconComponent = medicineIconMap[icon];

  if (!IconComponent) {
    const FallbackIcon = medicineIconMap[PRESCRIPTION_ITEM_ICON.PRODUCT_CORRELATED];
    return <FallbackIcon width={width} />;
  }

  return <IconComponent width={width} />;
};

export function PrescriptionPreviewModal(props: PrescriptionPreviewModalProps) {
  const {
    medicines,
    exams,
    referrals,
    medicalCertificates,
    reports,
    onNewPrescription,
    onResumePrescription,
    onClose,
    isLoading,
  } = props;
  const isMobile = useIsMobile();

  const hasMedicines = medicines && medicines.length > 0;
  const hasExams = exams && exams.length > 0;
  const hasReferrals = referrals && referrals.length > 0;
  const hasMedicalCertificates = medicalCertificates && medicalCertificates.length > 0;
  const hasReports = reports && reports.length > 0;

  const hasAnyItems = hasMedicines || hasExams || hasReferrals || hasMedicalCertificates || hasReports;

  useEffect(() => {
    if (!isMobile || !onClose) return;

    window.history.pushState({ modalOpen: true }, '');

    const handlePopState = (event: PopStateEvent) => {
      onClose();
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      if (window.history.state?.modalOpen) {
        window.history.back();
      }
    };
  }, [isMobile, onClose]);

  return (
    <>
      {!isMobile && onClose && (
        <IconButton
          aria-label="Fechar modal"
          onClick={onClose}
          position="absolute"
          top="15px"
          right="15px"
          size="md"
          variant="ghost"
          colorScheme="gray"
          borderRadius="full"
          zIndex={5}
          _hover={{ bg: 'gray.100' }}
        >
          <CloseSVG width={24} height={24} />
        </IconButton>
      )}
      <Box
        bg={'surface'}
        display="flex"
        flexDirection="column"
        height={isMobile ? '100%' : '100%'}
        w={'100%'}
        maxHeight={isMobile ? '100vh' : '70vh'}
        maxWidth={isMobile ? '100vw' : 'auto'}
        p={isMobile ? '20px' : '0'}
        pt={isMobile ? '40px' : '0'}
        position={isMobile ? 'fixed' : 'relative'}
        top={isMobile ? 0 : 'auto'}
        left={isMobile ? 0 : 'auto'}
        overflow="hidden"
        justifyContent={isMobile ? 'space-between' : 'flex-start'}
      >
        {isMobile && onClose && (
          <IconButton
            aria-label="Fechar modal"
            onClick={onClose}
            position="absolute"
            top="15px"
            right="15px"
            size="md"
            variant="ghost"
            colorScheme="gray"
            borderRadius="full"
            _hover={{ bg: 'gray.100' }}
          >
            <CloseSVG width={24} height={24} />
          </IconButton>
        )}

        <Box w={'100%'} mt={isMobile ? '10px' : '20px'} mb={isMobile ? '15px' : '20px'} flexShrink={0} display="flex">
          <Typography model="h2" weight="bold" textAlign="left">
            Prescrição em andamento encontrada
          </Typography>
        </Box>

        <Box
          backgroundColor={'#F2F3F5'}
          flex={1}
          overflowY="auto"
          mb={isMobile ? '20px' : '30px'}
          p={isMobile ? '15px' : '20px'}
          bg={'neutral.50'}
          borderRadius={'8px'}
          pr={isMobile ? '16px' : '20px'}
          minHeight={0}
          maxHeight={isMobile ? 'calc(100vh - 300px)' : 'none'}
          css={{
            '&::-webkit-scrollbar': {
              width: '8px',
              marginRight: '4px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
              borderRadius: '10px',
              margin: '10px 4px 10px 0',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#FFD3DE',
              borderRadius: '10px',
              border: '2px solid transparent',
              backgroundClip: 'padding-box',
              marginRight: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#FFC0D1',
              borderRadius: '10px',
              border: '2px solid transparent',
              backgroundClip: 'padding-box',
            },
            '&::-webkit-scrollbar-thumb:active': {
              background: '#FFADC4',
              borderRadius: '10px',
            },
            // Firefox
            scrollbarWidth: 'thin',
            scrollbarColor: '#FFD3DE transparent',
          }}
        >
          {hasMedicines && (
            <Box
              mb={hasExams || hasReferrals || hasMedicalCertificates || hasReports ? { base: '20px', md: '30px' } : '0'}
            >
              <Box mb={{ base: '10px', md: '15px' }}>
                <Typography model="h3" weight="bold">
                  Receita
                </Typography>
              </Box>
              {medicines.map((medicine, index) => (
                <Flex key={`medicine-${index}`} alignItems="start" mb={{ base: '10px', md: '12px' }}>
                  <Box
                    mr={{ base: '10px', md: '12px' }}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    minWidth={{ base: '24px', md: '28px' }}
                  >
                    {getIconForItem(medicine.icon, 24)}
                  </Box>
                  <Typography model="body" size="large" weight="regular" textAlign="left">
                    {medicine.description}
                  </Typography>
                </Flex>
              ))}
            </Box>
          )}

          {hasExams && (
            <Box mb={hasReferrals || hasMedicalCertificates || hasReports ? { base: '20px', md: '30px' } : '0'}>
              <Box mb={{ base: '10px', md: '15px' }}>
                <Typography model="h3" weight="bold">
                  Exames
                </Typography>
              </Box>
              {exams.map((exam, index) => (
                <Flex key={`exam-${index}`} alignItems="start" mb={{ base: '10px', md: '12px' }}>
                  <Box
                    mr={{ base: '10px', md: '12px' }}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    minWidth={{ base: '24px', md: '28px' }}
                  >
                    {<BakerIcon width={24} />}
                  </Box>
                  <Typography model="body" size="large" weight="regular" textAlign="left">
                    {exam.description}
                  </Typography>
                </Flex>
              ))}
            </Box>
          )}

          {hasReferrals && (
            <Box mb={hasMedicalCertificates || hasReports ? { base: '20px', md: '30px' } : '0'}>
              <Box mb={{ base: '10px', md: '15px' }}>
                <Typography model="h3" weight="bold">
                  Encaminhamento
                </Typography>
              </Box>
              {referrals.map((referral, index) => (
                <Flex key={`referral-${index}`} alignItems="start" mb={{ base: '10px', md: '12px' }}>
                  <Box
                    mr={{ base: '10px', md: '12px' }}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    minWidth={{ base: '24px', md: '28px' }}
                    mt={'2px'}
                  >
                    {<ForwardingIcon width={18} />}
                  </Box>
                  <Typography
                    model="body"
                    size="large"
                    weight="regular"
                    textAlign="left"
                    htmlText={referral.description}
                  >
                    {referral.description}
                  </Typography>
                </Flex>
              ))}
            </Box>
          )}

          {hasMedicalCertificates && (
            <Box mb={hasReports ? { base: '20px', md: '30px' } : '0'}>
              <Box mb={{ base: '10px', md: '15px' }}>
                <Typography model="h3" weight="bold">
                  Atestado
                </Typography>
              </Box>
              {medicalCertificates.map((certificate, index) => (
                <Flex key={`certificate-${index}`} alignItems="start" mb={{ base: '10px', md: '12px' }}>
                  <Box
                    mr={{ base: '10px', md: '12px' }}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    minWidth={{ base: '24px', md: '28px' }}
                    mt={'2px'}
                  >
                    {<CertificateIcon width={14} />}
                  </Box>
                  <Typography
                    model="body"
                    size="large"
                    weight="regular"
                    textAlign="left"
                    htmlText={certificate.description}
                  >
                    {certificate.description}
                  </Typography>
                </Flex>
              ))}
            </Box>
          )}

          {hasReports && (
            <Box>
              <Box mb={{ base: '10px', md: '15px' }}>
                <Typography model="h3" weight="bold">
                  Relatório
                </Typography>
              </Box>
              {reports.map((report, index) => (
                <Flex key={`report-${index}`} alignItems="start" mb={{ base: '10px', md: '12px' }}>
                  <Box
                    mr={{ base: '10px', md: '12px' }}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    minWidth={{ base: '24px', md: '28px' }}
                    mt={'2px'}
                  >
                    {<DocBoxIcon width={18} />}
                  </Box>
                  <Typography model="body" size="large" weight="regular" textAlign="left" htmlText={report.description}>
                    {report.description}
                  </Typography>
                </Flex>
              ))}
            </Box>
          )}

          {!hasAnyItems && (
            <Box p={'20px'}>
              <Typography model="body" size="normal" weight="regular" textAlign="center" color="neutral.500">
                Nenhum item encontrado na prescrição anterior.
              </Typography>
            </Box>
          )}
        </Box>

        <Flex
          gap={isMobile ? '10px' : '15px'}
          flexShrink={0}
          mb={isMobile ? '20px' : '0'}
          flexDirection={isMobile ? 'column' : 'row'}
        >
          <Button
            onClick={onNewPrescription}
            w={'100%'}
            h={isMobile ? '48px' : '40px'}
            variant="outline"
            disabled={isLoading}
          >
            Nova prescrição
          </Button>
          <Button
            onClick={onResumePrescription}
            w={'100%'}
            h={isMobile ? '48px' : '40px'}
            variant="solid"
            isLoading={isLoading}
            disabled={isLoading}
          >
            Retomar prescrição
          </Button>
        </Flex>
      </Box>
    </>
  );
}
